/**
 * Documents API hooks — TanStack Query layer over /api/documents.
 *
 * Uploads go to R2 via multipart/form-data. The server expects two form
 * fields: `file` (the binary) and `metadata` (a JSON string parsed through
 * `documentCreateInput`). The returned `Document` row uses an opaque
 * `storageKey`; the client never constructs or inspects it directly.
 *
 * confidentialRank controls read scope:
 *   Public  → any authenticated user
 *   Manager → admin + the employee's manager
 *   Admin   → admin only
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type { Document, Paginated } from '@shared/types';
import { documentCreateInput, documentUpdateInput } from '@shared/schemas/index.js';
import { z } from 'zod';

/** Metadata describing a document upload (parsed server-side from the `metadata` form field). */
export type DocumentMetadata = z.input<typeof documentCreateInput>;
export type DocumentUpdateBody = z.input<typeof documentUpdateInput>;

export const documentKeys = {
  all: ['documents'] as const,
  list: (params?: { employeeId?: string }) =>
    [...documentKeys.all, 'list', params ?? {}] as const,
};

/** List documents, optionally scoped to an employee. Staff-only on the server. */
export function useDocuments(params?: { employeeId?: string; enabled?: boolean }) {
  return useQuery({
    queryKey: documentKeys.list(params?.employeeId ? { employeeId: params.employeeId } : undefined),
    queryFn: () =>
      api.get<Paginated<Document>>(`/api/documents${qs({ employeeId: params?.employeeId })}`),
    enabled: params?.enabled ?? true,
  });
}

/**
 * Upload a document (Admin/Manager). Builds the multipart form from a `File`
 * and a metadata object, then POSTs via `api.upload`.
 */
export function useUploadDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, metadata }: { file: File; metadata: DocumentMetadata }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('metadata', JSON.stringify(metadata));
      return api.upload<Document>('/api/documents', formData);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

/** Update document metadata (Admin/Manager). */
export function useUpdateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DocumentUpdateBody }) =>
      api.put<Document>(`/api/documents/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

/** Delete a document (Admin only). Removes from R2 and the DB. */
export function useDeleteDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/api/documents/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}
